'use client';

import React, { useId, useMemo, useCallback, useRef } from 'react';
import { useRouter, useParams } from 'next/navigation';
import Link from 'next/link';
import { getAlgoliaResults } from '@algolia/autocomplete-js';
import { OnSubmitParams } from '@algolia/autocomplete-core';
import algoliasearch from '../../utils/algoliasearch';
import { styled } from '@mui/material';
import { Box } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import Autocomplete from '../Autocomplete';
import SearchResultItem from '../SearchResultItem';
import { useLocalizationContext } from '../LocalizationContext';
import { useAuthContext } from '../AuthProvider';
import { useSessionStorage } from 'usehooks-ts';
import { AutocompleteBoxProps, SettingsProps } from './AutoCompleteBox.types';
import { safeDataLayerPush } from '../../utils/safeDataLayerPush';
import { localizeHref } from '../../utils/localizeHref';
import { DEFAULT_LOCALE } from '../../utils/locales';
import { SORT_KEY, SORT_INITIALIZED_KEY } from '../../utils/searchSortStorage';
import aa from 'search-insights';
import { debouncePromise } from '../../utils/debouncePromise';

export interface templatesItemsProps {
  item: any;
  components: any;
}

export interface ResultItemProps {
  router: any;
  href: string;
  children: React.JSX.Element;
}

const ResultItem = ({ router, href, children }: ResultItemProps) => {
  return (
    <RouterContext.Provider value={router}>
      <Link href={href}>{children}</Link>
    </RouterContext.Provider>
  );
};

const IS_SIGNAL_3 = process.env.SITE === 'IAS-KB-SIGNAL3-SITE';

const calculateFilters = (filters: string[], facetFilters: string[]) => {
  const baseFilters = [...filters];
  if (!!facetFilters.length) {
    baseFilters.push(`(${facetFilters.join(' OR ')})`);
  }
  return baseFilters.join(' AND ');
};

export const AutocompleteBox = ({ settings }: AutocompleteBoxProps) => {
  const theme = useTheme();
  const router = useRouter();
  const params = useParams();
  const localization = useLocalizationContext();
  const matchesDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  // Generate a stable unique ID for this component instance
  const reactId = useId();
  const instanceId = reactId.replace(/:/g, '');

  // Get locale from route params (set by [locale] dynamic segment)
  const locale = (params?.locale as string) || DEFAULT_LOCALE;

  const { variant, searchResultsUrl, indexName, algoliaApiKey } = settings as SettingsProps;
  const { apps } = useAuthContext();

  const [sort] = useSessionStorage(SORT_KEY, indexName ?? '');
  const [sortInitialized] = useSessionStorage(SORT_INITIALIZED_KEY, false);
  const index = sortInitialized && sort ? sort : indexName;

  const placeholder = localization['header.search.placeholder']?.shortTextValue ?? 'Search our knowledge base';
  const mobileText = localization['autocomplete.searchMobile.placeholder']?.shortTextValue ?? 'Search';

  // Memoize searchClient to prevent recreation on every render
  // Note: Must be called before early returns to maintain hook order
  const searchClient = useMemo(
    () => (algoliaApiKey ? algoliasearch(process.env.ALGOLIA_APP_ID as string, algoliaApiKey) : null),
    [algoliaApiKey]
  );

  // Memoize filters to prevent new array reference on every render
  const filters = useMemo(() => {
    const baseFilters: string[] = [`locale:${locale}`];
    if (locale !== DEFAULT_LOCALE) {
      baseFilters.push(`translatedInLocale:true`);
    }
    if (IS_SIGNAL_3) {
      baseFilters.push(`hasSsoGroups:true`);
    }
    return baseFilters;
  }, [locale]);

  // Memoize facetFilters to prevent new array reference on every render
  const facetFilters = useMemo(() => {
    if (IS_SIGNAL_3) {
      return apps?.map((app: string) => `ssoGroups:${app}`) ?? [];
    }
    return [];
  }, [apps]);

  // Memoize handleSubmit to prevent recreation on every render
  const handleSubmit = useCallback(
    (event: OnSubmitParams<any>) => {
      const searchQuery: string = event.state.query.trim();

      if (searchQuery === '' || !searchResultsUrl) {
        event.setQuery('');
        return;
      }

      const urlPath = global.location?.pathname;
      safeDataLayerPush({
        event: 'Algolia Search Button Click Header',
        isCustomEvent: 1,
        url: urlPath,
        query: searchQuery
      });

      // App Router requires string URLs with query params
      const localizedSearchUrl = localizeHref(searchResultsUrl, locale);
      const searchUrl = `${localizedSearchUrl}?query=${encodeURIComponent(searchQuery)}`;
      router.push(searchUrl);
    },
    [locale, searchResultsUrl, router]
  );

  // Memoize noResults text
  const noResultsText = localization['autocomplete.searchNoResults.label']?.shortTextValue ?? 'No Results';

  /**
   * One search when somebody stops typing, not one per keystroke.
   *
   * getAlgoliaResults returns a requester that autocomplete resolves itself, so
   * the debounce goes around the sources rather than around the fetch. That is
   * Algolia's own recipe and it delays the whole request. Held in a ref so the
   * timer survives re-renders. 250ms collapses a word and still feels live.
   */
  const debouncedSources = useRef(debouncePromise((sources: any) => Promise.resolve(sources), 250));

  /**
   * Report the click to Algolia when somebody opens a suggestion.
   *
   * The header box is where most searching starts and none of it was measured:
   * no events at all, and no clickAnalytics on the request, so the results
   * carried no queryID to attribute a click to. Both are fixed here. The results
   * page sends its own events through react-instantsearch.
   */
  const sendAutocompleteClick = useCallback(
    (item: any) => {
      const queryID = item?.__autocomplete_queryID;
      const position = item?.__autocomplete_indexName ? item?.__position : undefined;
      if (!queryID || !index) return;
      try {
        aa('clickedObjectIDsAfterSearch', {
          index: index as string,
          eventName: 'Autocomplete Suggestion Clicked',
          objectIDs: [item.objectID],
          positions: [position ?? 1],
          queryID
        });
      } catch {
        // Analytics must never take the navigation down with it.
      }
    },
    [index]
  );

  // Memoize getSources to prevent recreation on every render
  // Note: index is set once indexName is known (early return); matches Page.tsx InstantSearch index resolution
  const getSources = useCallback(
    ({ query }: any) =>
      debouncedSources.current([
      {
        sourceId: index as string,
        getItems() {
          return getAlgoliaResults({
            searchClient: searchClient as any,
            queries: [
              {
                indexName: index as string,
                params: {
                  query,
                  // Without this the response carries no queryID, so a click
                  // cannot be attributed to the search that produced it.
                  clickAnalytics: true,
                  filters: calculateFilters(filters, facetFilters)
                }
              }
            ]
          });
        },
        onSelect({ item }: any) {
          sendAutocompleteClick(item);
        },
        templates: {
          item({ item, components }: templatesItemsProps) {
            item.path = localizeHref(item?.path, item?.locale ?? locale);
            return (
              <ResultItem router={router} href={item.path}>
                <components.SearchResultItem
                  hit={item}
                  router={router}
                  data-testid="SearchResultItem"
                  components={components}
                />
              </ResultItem>
            );
          },
          noResults() {
            return noResultsText;
          }
        }
      }
      ]),
    [index, searchClient, filters, facetFilters, locale, router, noResultsText, sendAutocompleteClick]
  );

  // Memoize components object to prevent recreation on every render
  const autocompleteComponents = useMemo(() => ({ SearchResultItem }), []);

  // Early returns after all hooks to maintain consistent hook order
  if (!algoliaApiKey || !indexName || !index) return null;

  // Use unique ID for panel container to prevent conflicts during locale switches
  const panelContainerId = `autocomplete-container-${instanceId}`;

  return (
    <Root variant={variant} id={panelContainerId} sx={{ position: 'relative' }} data-testid="AutocompleteBox">
      <Autocomplete
        key={locale}
        instanceId={instanceId}
        onSubmit={handleSubmit}
        placeholder={matchesDesktop ? placeholder : mobileText}
        components={autocompleteComponents}
        getSources={getSources}
        filters={filters}
        facetFilters={facetFilters}
        variant={variant}
      />
    </Root>
  );
};

const Root = styled(Box, {
  name: 'AutocompleteBox',
  slot: 'Root',
  shouldForwardProp: (prop: any) => prop !== 'variant',
  overridesResolver: (_: any, styles: any) => [styles.root]
})<{ variant?: string }>``;

export default AutocompleteBox;
